import React, { ComponentProps } from 'react';
import {
  BlockInteractions,
  Views as BlockInteractionsViews,
  DISPLAY_TYPE,
  createButtonObject,
} from '../../components/organisms/BlockInteractions';
import { Icons } from '../../components/atoms/SVG';
import { Namespace } from '../../translation/namespace';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { DefaultView, DeleteAction, EditView, Views } from '../../types/BlockWithViews';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { useContentWrapperContext } from '../../components/blocks/ContentWrapper/ContentWrapperContext';
import { RichtextEditView } from './views/RichtextEditView';
import { CollapsibleWysiwygView } from '../../_lib/components/Wysiwyg';
import { useContentWrapperDndContext } from '../../components/blocks/ContentWrapper/ContentWrapperDndProvider';
import { useLayoutGroupContext } from '../../components/blocks/LayoutGroup/LayoutGroup';
import { headlineHierarchy } from '../../components/organisms/BlockInteractions/BlockInteractions.utils';

type RichtextProps = Views<DefaultView<CollapsibleWysiwygView> & EditView<RichtextEditView> & DeleteAction>;

function RichText(props: RichtextProps) {
  const { t } = useSonTranslation('RichText');
  const { uuid, elementType } = useBlockContext();
  const { refreshCount } = useRefreshBlock();

  const contentWrapperContext = useContentWrapperContext();
  const contentWrapperDndContext = useContentWrapperDndContext();
  const layoutGroupProps = useLayoutGroupContext();

  const createButtonsObject = () => {
    const EditButton = createButtonObject(
      BlockInteractionsViews.EDIT,
      Icons.EDIT_PENCIL,
      `${Namespace.RichText}:buttons.edit`,
      DISPLAY_TYPE.COLLAPSIBLE,
      <RichtextEditView {...props.views.edit} />,
    );

    const DeleteButton = createButtonObject(
      BlockInteractionsViews.DELETE,
      Icons.ERASER,
      `${Namespace.RichText}:buttons.delete`,
      DISPLAY_TYPE.MODAL,
    );

    return { ...EditButton, ...DeleteButton };
  };

  return (
    <BlockInteractions
      key={uuid}
      uuid={uuid}
      contentWrapperContext={contentWrapperContext}
      contentWrapperDndContext={contentWrapperDndContext}
      isNestedInCollapsibleOrLayoutGroup={!!layoutGroupProps}
      headline={{
        text: t('headline'),
        hierarchy: headlineHierarchy(2, contentWrapperContext?.parentIndex, contentWrapperContext?.grandParentIndex),
        visible: true,
      }}
      alwaysShowInfo={false}
      views={props.views}
      activeView={props.activeView}
      buttons={createButtonsObject()}
      elementType={elementType}
      refreshCount={refreshCount}
    >
      <CollapsibleWysiwygView {...props.views.default} />
    </BlockInteractions>
  );
}

export const RichTextBlock = asFetchableBlock(RichText, BlockType.richtext);
export type RichTextBlock = ComponentProps<typeof RichTextBlock>;
